/**
 * ChatPage - AI Conversation Interface
 *
 * Two-column layout with conversation list sidebar and active conversation.
 * Uses SSE streaming for progressive AI response rendering.
 */

import {
  Component,
  Show,
  For,
  createSignal,
  createEffect,
  onCleanup,
  onMount,
} from "solid-js";
import { EmptyState, Button, Spinner, AiErrorBoundary } from "../components/ui";
import { MessageThread, ChatInput } from "../components/chat";
import { currentUser } from "../stores/currentUserStore";
import { ProposalReviewModal } from "../components/proposals";
import { Tooltip, useTooltips } from "../components/Tooltip";
import {
  useConversations,
  useProposals,
  useToast,
  useWebSocket,
} from "../hooks";
import { useFirstVisit } from "../utils/useFirstVisit";
import type {
  Conversation,
  BoardEvent,
  ProposalId,
  ProposedActionId,
  MessageId,
} from "../api/types";
import { useNavigate, useLocation } from "@solidjs/router";
import { useProjectId, useProjectPrefix } from "../contexts/ProjectContext";
import { setChatDrawerOpen } from "../stores/chatDrawerStore";
import { Modal } from "../components/ui/Modal";

export const ChatPage: Component = () => {
  const projectId = useProjectId();
  const projectPrefix = useProjectPrefix();
  const navigate = useNavigate();
  const location = useLocation();

  const handleCollapse = () => {
    setChatDrawerOpen(true);
    const from = (location.state as { from?: string } | null)?.from;
    navigate(from ?? "/board");
  };

  const { showError } = useToast();

  const tooltips = useTooltips();
  const isFirstVisit = useFirstVisit("chat");
  const [chatInputRef, setChatInputRef] = createSignal<HTMLElement | null>(
    null,
  );
  const [showChatTooltip, setShowChatTooltip] = createSignal(false);

  const [showShortcutsModal, setShowShortcutsModal] = createSignal(false);

  let textareaEl: HTMLTextAreaElement | undefined;

  // Focused message index for Ctrl+Up/Down navigation (-1 = none)
  const [focusedMsgIdx, setFocusedMsgIdx] = createSignal(-1);

  const {
    conversations,
    loading: conversationsLoading,
    selectedConversation,
    selectedLoading,
    messages,
    isResponding,
    streamingContent,
    select,
    create,
    send,
    cancel,
    updateTitle,
    toggleStar,
    getProposal,
    applyProposalUpdate,
    refreshProposal,
    handleWebSocketEvent: handleConversationWsEvent,
    deleteMessageAndResend,
    retryMessage,
  } = useConversations(projectId, {
    // The inline report for a dropped edit sits in the conversation the user
    // just left, so there is nothing on screen to say it twice (FLOW-818).
    onDroppedEdit: showError,
  });

  const {
    selectedProposal,
    selectedLoading: proposalLoading,
    select: selectProposal,
    approve,
    reject,
    refetch: refetchProposals,
    handleWebSocketEvent: handleProposalWsEvent,
  } = useProposals(projectId);

  // WebSocket for real-time updates (e.g., AI continuation after proposal approval)
  useWebSocket({
    onMessage: (event: BoardEvent) => {
      handleConversationWsEvent(event);
      handleProposalWsEvent(event);
      // Refresh only the one proposal that changed, and only if it belongs to the
      // conversation being viewed — never fan out across the project's proposals.
      if (
        event.type === "ProposalUpdated" ||
        event.type === "ProposalCompleted"
      ) {
        const { proposal_id } = event.payload;
        if (getProposal(proposal_id)) {
          void refreshProposal(proposal_id);
        }
      }
    },
  });

  // Reset focused message index when conversation changes
  createEffect(() => {
    void selectedConversation();
    setFocusedMsgIdx(-1);
  });

  onMount(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const metaOrCtrl = e.metaKey || e.ctrlKey;
      const tag = (e.target as HTMLElement)?.tagName;
      const isInInput =
        tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";

      if (e.key === "?" && !isInInput && !metaOrCtrl) {
        e.preventDefault();
        setShowShortcutsModal((v) => !v);
        return;
      }

      if (e.key === "Escape") {
        if (showShortcutsModal()) {
          setShowShortcutsModal(false);
          return;
        }
        if (focusedMsgIdx() !== -1) {
          setFocusedMsgIdx(-1);
          textareaEl?.focus();
          return;
        }
        // If focus is in textarea, blur it (handled by ChatInput via Esc key)
        return;
      }

      // Ctrl/Cmd+C → copy focused message (only when a message is focused and no text is selected)
      if (metaOrCtrl && e.key === "c") {
        const idx = focusedMsgIdx();
        if (idx === -1) return; // no message focused — let browser handle
        if (window.getSelection()?.toString()) return; // text selected — let browser copy it
        const msg = messages()[idx];
        if (!msg) return;
        e.preventDefault();
        navigator.clipboard.writeText(msg.content).then(() => {
          setCopiedMsgIdx(idx);
          setTimeout(() => setCopiedMsgIdx(-1), 2000);
        });
        return;
      }

      if (metaOrCtrl && e.key === "ArrowUp") {
        e.preventDefault();
        const msgs = messages();
        if (!msgs.length) return;
        setFocusedMsgIdx((idx) => {
          const next = idx <= 0 ? msgs.length - 1 : idx - 1;
          focusMessageElement(next);
          return next;
        });
        return;
      }

      if (metaOrCtrl && e.key === "ArrowDown") {
        e.preventDefault();
        const msgs = messages();
        if (!msgs.length) return;
        setFocusedMsgIdx((idx) => {
          if (idx === -1 || idx >= msgs.length - 1) {
            // At the end → return focus to input
            textareaEl?.focus();
            return -1;
          }
          const next = idx + 1;
          focusMessageElement(next);
          return next;
        });
        return;
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    onCleanup(() => document.removeEventListener("keydown", handleKeyDown));
  });

  const focusMessageElement = (idx: number) => {
    // Focus the nth message bubble's copy button for keyboard accessibility
    const bubbles = document.querySelectorAll<HTMLElement>("[data-msg-idx]");
    const target = Array.from(bubbles).find(
      (el) => el.dataset.msgIdx === String(idx),
    );
    if (target) {
      target.focus();
      target.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };

  // Tooltip setup - show chat input tooltip on first visit
  createEffect(() => {
    if (
      isFirstVisit() &&
      !tooltips.isDismissed("chat-ai-input") &&
      !conversationsLoading()
    ) {
      // Wait for ChatInput to render, then find the textarea
      const timer = setTimeout(() => {
        const textarea = document.querySelector("textarea") as HTMLElement;
        if (textarea) {
          setChatInputRef(textarea);
          setShowChatTooltip(true);
        }
      }, 500);
      onCleanup(() => clearTimeout(timer));
    }
  });

  const handleDismissChatTooltip = () => {
    tooltips.dismiss("chat-ai-input");
    setShowChatTooltip(false);
  };

  // Handle sending a message (POST-only, no SSE)
  const handleSend = async (content: string) => {
    if (content === "/clear") {
      try {
        await create();
      } catch {
        showError("Failed to create new conversation.");
      }
      return;
    }

    let conv = selectedConversation();
    if (!conv) {
      await create();
      conv = selectedConversation();
    }

    try {
      // Send message and get AI response
      // The send() function handles optimistic UI updates and adds the assistant message
      const response = await send(content);

      // If AI created a proposal, refresh the pending list and hydrate the new
      // proposal so its inline card renders without a per-message fetch.
      if (response.has_proposal) {
        refetchProposals();
        if (response.proposal_id) {
          void refreshProposal(response.proposal_id);
        }
      }
    } catch {
      // Reported inline on the failed message, with a Retry button — a toast
      // on top of that says the same thing twice (FLOW-692).
    }
  };

  // Handle retrying a failed message (mirrors handleSend's error handling)
  const handleRetryMessage = async (messageId: MessageId) => {
    try {
      const response = await retryMessage(messageId);

      // If AI created a proposal, refresh the pending list and hydrate the new
      // proposal so its inline card renders without a per-message fetch.
      if (response.has_proposal) {
        refetchProposals();
        if (response.proposal_id) {
          void refreshProposal(response.proposal_id);
        }
      }
    } catch {
      // Same as handleSend: the message's inline error indicator is the only
      // report (FLOW-692).
    }
  };

  const handleNewConversation = async () => {
    await create();
  };

  // Inline proposal actions (in the chat thread). Wrap approve/reject so the
  // returned updated proposal flows back into the conversation store, re-rendering
  // the inline card reactively — no per-card refetch.
  const handleInlineApprove = async (
    id: ProposalId,
    actionIds?: ProposedActionId[],
  ) => {
    const result = await approve(id, actionIds);
    applyProposalUpdate(result.proposal);
    return result;
  };

  const handleInlineReject = async (id: ProposalId) => {
    const result = await reject(id);
    applyProposalUpdate(result.proposal);
    return result;
  };

  // Handle proposal actions (for the modal - kept for fallback)
  // AI continuation messages arrive asynchronously via WebSocket MessageAdded events
  const handleApproveProposal = async (actionIds?: string[]) => {
    const proposal = selectedProposal();
    if (!proposal) return;
    await approve(proposal.id, actionIds);
  };

  const handleRejectProposal = async () => {
    const proposal = selectedProposal();
    if (!proposal) return;
    await reject(proposal.id);
  };

  // ↑ in empty input → enter edit mode on last user message
  const handleUpArrowEmpty = () => {
    const msgs = messages();
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === "user") {
        // Trigger edit via deleteMessageAndResend flow — we need to fire the
        // enterEditMode in MessageBubble. We do it by signaling via a dedicated
        // signal that MessageThread reads.
        setEditTargetMessageId(msgs[i].id);
        return;
      }
    }
  };

  // Signal to tell MessageThread which message to auto-enter edit mode on
  const [editTargetMessageId, setEditTargetMessageId] = createSignal<
    string | null
  >(null);

  // Signal to flash the "copied" checkmark on a specific message index
  const [copiedMsgIdx, setCopiedMsgIdx] = createSignal(-1);

  return (
    <div class="h-full flex">
      {/* Conversation List Sidebar */}
      <aside class="w-64 border-r border-line bg-surface flex flex-col overflow-hidden">
        <div class="p-4 border-b border-line">
          <Button
            variant="primary"
            class="w-full"
            onClick={handleNewConversation}
          >
            <PlusIcon class="w-4 h-4 mr-2" />
            New Chat
          </Button>
        </div>

        <div class="flex-1 overflow-y-auto">
          <Show
            when={!conversationsLoading()}
            fallback={
              <div class="flex items-center justify-center py-8">
                <Spinner size="sm" />
              </div>
            }
          >
            <Show
              when={conversations()?.length}
              fallback={
                <div class="p-4 text-sm text-ink-3 text-center">
                  No conversations yet
                </div>
              }
            >
              <ul class="py-2">
                <For each={conversations()}>
                  {(conv) => (
                    <ConversationItem
                      conversation={conv}
                      active={selectedConversation()?.id === conv.id}
                      onClick={() => select(conv.id)}
                      onUpdateTitle={(title) => updateTitle(conv.id, title)}
                      onToggleStar={() => toggleStar(conv.id)}
                    />
                  )}
                </For>
              </ul>
            </Show>
          </Show>
        </div>
      </aside>

      {/* Main Chat Area */}
      <main class="flex-1 flex flex-col min-w-0 overflow-hidden bg-paper">
        <div class="h-12 px-4 flex items-center justify-end border-b border-line bg-surface shrink-0">
          <button
            type="button"
            onClick={handleCollapse}
            class="p-2 text-ink-3 hover:text-ink hover:bg-sunken rounded transition-colors"
            aria-label="Collapse to sidebar"
            title="Collapse to sidebar"
          >
            <CollapseIcon />
          </button>
        </div>
        <Show
          when={selectedConversation()}
          fallback={
            <div class="flex-1 flex items-center justify-center">
              <EmptyState
                icon={<ChatIcon class="w-12 h-12" />}
                title="Start a Conversation"
                description="Select an existing conversation or start a new one to chat with the AI assistant."
                action={
                  <Button variant="primary" onClick={handleNewConversation}>
                    <PlusIcon class="w-4 h-4 mr-2" />
                    New Chat
                  </Button>
                }
              />
            </div>
          }
        >
          {/* Messages */}
          <Show
            when={!selectedLoading()}
            fallback={
              <div class="flex-1 flex items-center justify-center">
                <Spinner size="lg" />
              </div>
            }
          >
            {/* Only the thread is boundaried, not ChatInput: the sidebar, the
                collapse control and the input all stay live, so one malformed
                message doesn't cost the user the page. */}
            <AiErrorBoundary
              surface="Chat"
              manualPath="You can start a new conversation from the sidebar, or carry on from the board."
            >
              <MessageThread
                messages={messages()}
                isResponding={isResponding()}
                streamingContent={streamingContent()}
                onApproveProposal={handleInlineApprove}
                onRejectProposal={handleInlineReject}
                onProposalActioned={() => refetchProposals()}
                getProposal={getProposal}
                userName={currentUser()?.name}
                userAvatarUrl={currentUser()?.avatar_url}
                onDeleteMessageAndResend={deleteMessageAndResend}
                editTargetMessageId={editTargetMessageId}
                onEditTargetConsumed={() => setEditTargetMessageId(null)}
                copiedMsgIdx={copiedMsgIdx}
                onRetryMessage={handleRetryMessage}
                onSendFollowUp={handleSend}
                projectId={projectId()}
                projectPrefix={projectPrefix()}
              />
            </AiErrorBoundary>
          </Show>

          {/* Input */}
          <ChatInput
            onSend={handleSend}
            disabled={isResponding()}
            onCancel={cancel}
            placeholder="Ask the AI assistant..."
            onRef={(el) => {
              textareaEl = el;
            }}
            onUpArrowEmpty={handleUpArrowEmpty}
            onShowShortcuts={() => setShowShortcutsModal(true)}
          />
        </Show>
      </main>

      {/* Proposal Review Modal */}
      <ProposalReviewModal
        proposal={selectedProposal()}
        loading={proposalLoading()}
        onClose={() => selectProposal(null)}
        onApprove={handleApproveProposal}
        onReject={handleRejectProposal}
        onStartNewChat={handleNewConversation}
      />

      {/* Feature Discovery Tooltip */}
      <Tooltip
        anchor={chatInputRef()}
        content="Describe what you want in plain language — I can create, update, and organise work items for you."
        id="chat-ai-input"
        onDismiss={handleDismissChatTooltip}
        show={showChatTooltip()}
      />

      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcutsModal
        open={showShortcutsModal()}
        onClose={() => setShowShortcutsModal(false)}
      />
    </div>
  );
};

const KeyboardShortcutsModal: Component<{
  open: boolean;
  onClose: () => void;
}> = (props) => {
  const isMac =
    typeof navigator !== "undefined" &&
    navigator.platform.toLowerCase().includes("mac");
  const mod = isMac ? "⌘" : "Ctrl";

  const shortcuts: { keys: string[]; description: string }[] = [
    { keys: [`${mod}+Enter`], description: "Send message" },
    { keys: ["Enter"], description: "Send message" },
    { keys: ["Shift+Enter"], description: "New line in message" },
    { keys: ["Esc"], description: "Clear input / deselect message" },
    { keys: ["↑"], description: "Edit last message (when input is empty)" },
    { keys: [`${mod}+↑`], description: "Navigate to previous message" },
    { keys: [`${mod}+↓`], description: "Navigate to next message" },
    { keys: ["?"], description: "Show this help" },
  ];

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      title="Keyboard Shortcuts"
      size="sm"
    >
      <dl class="divide-y divide-line">
        <For each={shortcuts}>
          {(shortcut) => (
            <div class="flex items-center justify-between py-2.5 gap-4">
              <dd class="text-sm text-ink-2">{shortcut.description}</dd>
              <dt class="flex gap-1 shrink-0">
                <For each={shortcut.keys}>
                  {(key) => (
                    <kbd class="inline-flex items-center px-1.5 py-0.5 rounded border border-line bg-sunken text-xs font-mono text-ink-2">
                      {key}
                    </kbd>
                  )}
                </For>
              </dt>
            </div>
          )}
        </For>
      </dl>
    </Modal>
  );
};

interface ConversationItemProps {
  conversation: Conversation;
  active: boolean;
  onClick: () => void;
  onUpdateTitle: (title: string) => void;
  onToggleStar: () => void;
}

const ConversationItem: Component<ConversationItemProps> = (props) => {
  const title = () =>
    props.conversation.title ||
    `Chat ${formatDate(props.conversation.created_at)}`;

  const [editing, setEditing] = createSignal(false);
  const [editValue, setEditValue] = createSignal("");

  const startEditing = (e: MouseEvent) => {
    e.stopPropagation();
    setEditValue(props.conversation.title || "");
    setEditing(true);
  };

  const commitEdit = () => {
    const val = editValue().trim();
    if (val && val !== props.conversation.title) {
      props.onUpdateTitle(val);
    }
    setEditing(false);
  };

  const cancelEdit = () => {
    setEditing(false);
  };

  const handleStarClick = (e: MouseEvent) => {
    e.stopPropagation();
    void props.onToggleStar();
  };

  return (
    <li>
      <div
        class={
          "relative group px-4 py-3 text-sm transition-colors cursor-pointer " +
          (props.active
            ? "bg-sunken text-ink border-r-2 border-r-ink"
            : "text-ink-2 hover:bg-sunken")
        }
        onClick={props.onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            props.onClick();
          }
        }}
      >
        <Show
          when={!editing()}
          fallback={
            <input
              type="text"
              class="w-full text-sm font-medium bg-surface border border-line rounded px-1.5 py-0.5 focus-ring"
              value={editValue()}
              onInput={(e) => setEditValue(e.currentTarget.value)}
              onBlur={commitEdit}
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") commitEdit();
                if (e.key === "Escape") cancelEdit();
              }}
              onClick={(e) => e.stopPropagation()}
              maxLength={255}
              ref={(el) => setTimeout(() => el.focus(), 0)}
            />
          }
        >
          <div class="font-medium truncate" onDblClick={startEditing}>
            {title()}
          </div>
        </Show>
        <div class="flex items-center justify-between mt-1">
          <span class="text-xs text-ink-3">
            {formatDate(props.conversation.updated_at)}
          </span>
          <button
            type="button"
            class={
              "shrink-0 p-0.5 -mr-0.5 rounded transition-colors " +
              (props.conversation.is_starred
                ? "text-signal-amber hover:text-ink"
                : "text-ink-3 opacity-0 group-hover:opacity-100 focus:opacity-100 hover:text-ink")
            }
            aria-label={
              props.conversation.is_starred
                ? "Unstar conversation"
                : "Star conversation"
            }
            title={
              props.conversation.is_starred
                ? "Unstar conversation"
                : "Star conversation"
            }
            onClick={handleStarClick}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <StarIcon filled={props.conversation.is_starred} />
          </button>
        </div>
      </div>
    </li>
  );
};

const StarIcon: Component<{ filled: boolean }> = (props) => (
  <svg
    class="w-4 h-4"
    fill={props.filled ? "currentColor" : "none"}
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
    />
  </svg>
);

function formatDate(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diff = now.getTime() - date.getTime();

  if (diff < 24 * 60 * 60 * 1000 && date.getDate() === now.getDate()) {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }

  if (diff < 7 * 24 * 60 * 60 * 1000) {
    return date.toLocaleDateString([], { weekday: "short" });
  }

  return date.toLocaleDateString([], { month: "short", day: "numeric" });
}

const PlusIcon: Component<{ class?: string }> = (props) => (
  <svg
    class={props.class ?? "w-5 h-5"}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M12 4v16m8-8H4"
    />
  </svg>
);

const ChatIcon: Component<{ class?: string }> = (props) => (
  <svg
    class={props.class ?? "w-5 h-5"}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="1.5"
      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
    />
  </svg>
);

const CollapseIcon: Component<{ class?: string }> = (props) => (
  <svg
    class={props.class ?? "w-5 h-5"}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M9 4H4v5M15 4h5v5M15 20h5v-5M9 20H4v-5"
    />
  </svg>
);

export default ChatPage;
